import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from 'services/urls';
import { useCreateDealDirectoryTree, useCreateMultipleDealDocuments } from '.';
import { buildDirectoryTree, collectFullPaths, normalizeFileFormat } from '../helpers';
import { IFlatDirectory } from '../types';

export interface UseDocumentUploaderOptions {
  dealId: number;
  organizationId: string;
  selectedDirectoryId: number;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  clearAttachmentsStates: () => void;
  setAwsUploadedFiles: (files: any[]) => void;
  setSelectedDirectoryId: (id: number) => void;
  setShowUploadModal: (open: boolean) => void;
  setNewFoldersToDisplay: (folders: string[]) => void;
  setNewFilesToDisplay: (files: string[]) => void;
}

export interface FileMeta {
  id: string;
  file: File;
  relativePath?: string;
}

export interface UploadedFileMeta {
  id: string;
  bucketName: string;
  objectKey: string;
}

export function useDocumentUploader({
    dealId,
    organizationId,
    selectedDirectoryId,
    showSuccess,
    showError,
    clearAttachmentsStates,
    setAwsUploadedFiles,
    setSelectedDirectoryId,
    setShowUploadModal,
    setNewFoldersToDisplay,
    setNewFilesToDisplay,
}: UseDocumentUploaderOptions) {
    const queryClient = useQueryClient();
    const { mutateAsync: createDirectoriesAsync } = useCreateDealDirectoryTree();
    const { mutate: createDealDocuments } = useCreateMultipleDealDocuments();

    const upload = useCallback(
        async (
            selectedHTML5FileToUpload: FileMeta[],
            awsUploadedFiles: UploadedFileMeta[],
        ) => {
            try {
                // STEP 1: Build nested directory tree
                const directoryTree = buildDirectoryTree(selectedHTML5FileToUpload);

                // STEP 2: Create directories via API
                let createdDirectories: IFlatDirectory[] = [];
                if (directoryTree.length > 0) {
                    createdDirectories = await createDirectoriesAsync({
                        postData: { parentDirectoryId: selectedDirectoryId, directoryTree },
                        dealId,
                    });
                }

                // STEP 3: Build path -> ID mapping
                const pathToIdMap = collectFullPaths(createdDirectories);

                // STEP 4: Prepare postData array
                const postFiles = selectedHTML5FileToUpload.map(file => {
                    const uploadedFile = awsUploadedFiles.find(f => f.id === file.id)!;
                    const pathParts = file.relativePath?.split('/').filter(Boolean) ?? [];
                    const folderPath = pathParts.slice(0, -1).join('/');
                    const directoryId = pathToIdMap[folderPath] ?? (selectedDirectoryId || null);

                    return {
                        dealId,
                        organizationId,
                        directoryId,
                        name: file.file.name,
                        extension: normalizeFileFormat(file.file.type)!,
                        size: Math.round(file.file.size / 1000),
                        s3Bucket: uploadedFile.bucketName,
                        s3Path: uploadedFile.objectKey,
                        activities: [],
                    };
                });

                // STEP 5: Upload documents via mutation
                createDealDocuments(
                    { postData: postFiles },
                    {
                        onSuccess: () => {
                            showSuccess(
                                `Document${postFiles.length > 1 ? 's' : ''} uploaded successfully`,
                            );
                            setAwsUploadedFiles([]);
                            clearAttachmentsStates();
                            setSelectedDirectoryId(0);
                            setShowUploadModal(false);
                            setNewFoldersToDisplay([]);
                            setNewFilesToDisplay([]);
                            queryClient.invalidateQueries({
                                queryKey: [QUERY_KEYS.DEAL_DIRECTORY_TREE, dealId],
                            });
                        },
                        onError: () => {
                            showError(
                                `Failed to upload document${postFiles.length > 1 ? 's' : ''}. Please try again.`,
                            );
                        },
                    },
                );
            } catch (err) {
                showError(
                    'An error occurred while creating directories or uploading files.',
                );
            }
        },
        [
            dealId,
            organizationId,
            selectedDirectoryId,
            createDirectoriesAsync,
            createDealDocuments,
            showSuccess,
            showError,
            clearAttachmentsStates,
            setAwsUploadedFiles,
            setSelectedDirectoryId,
            setShowUploadModal,
            setNewFoldersToDisplay,
            setNewFilesToDisplay,
            queryClient,
        ],
    );

    return upload;
}
